import { IFormStep } from '../../utils/formStep';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import getRoles from '../../../../helpers/role';
import i18n from 'i18next';
import iconDelete from '../img/icon-delete.png';
import InputField from '../../../form/inputField';
import DropDownField from '../../../form/dropdownField';
import getIssuers from '../../../../helpers/issuers';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import { IIssuerModel } from '../../../../helpers/models/issuerModel';
import { ELanguage } from '../../utils/language';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import { OrganizationStep3FormResultKeys } from './organizationStep3FormModel';
import { IRegulationModel } from '../../../../helpers/models/regulationModel';
import getRegulations from '../../../../helpers/regulations';
import FormIssuerModal, { IFormIssuerRef } from '../../../modal/instances/formIssuerModal';
import { handleInputWithLabelOnBlur, handleInputWithLabelOnFocus } from '../../registration';
import { FORM_FIRSTNAME_MAX_LENGTH, FORM_LASTNAME_MAX_LENGTH } from '../../utils/formValidation';
import { ProviderModel } from '../../../../helpers/models/providerModel';
import PopoverContainer from '../../../popover/popover';

import {
    getDateMaximumString,
    getDateMinimumString,
    handleCustomValidityOfDateFieldWithMinMax
} from '../../../form/dateField';

export interface IOrganizationStep3ProviderProps extends WithTranslation, IFormStep {
    readonly?: boolean;
    index: number;
    handleOnDeleteClick?: any;

    /*
     * Providers overwrite has been added because DOM changes were not correctly handled by REACT states
     * For some reason, providers element wasn't correctly update according to his default value
     * */
    providersOverwrite?: ProviderModel[];
}

const OrganizationStep3Provider = (props: IOrganizationStep3ProviderProps) => {
    const { t, index, handleOnDeleteClick, readonly, account, providersOverwrite } = props;

    const [selectedRole, setSelectedRole] = useState<number>(
        +(account?.provider[index]?.role as IRoleModel)?.id || -1
    );
    const [selectedIssuer, setSelectedIssuer] = useState<number>(
        +(account?.provider[index]?.issuer as IIssuerModel)?.id || -1
    );
    const [roles, setRoles] = useState<IRoleModel[]>([]);
    const [regulations, setRegulations] = useState<IRegulationModel[]>([]);
    const [issuers, setIssuers] = useState<IIssuerModel[]>([]);
    const ref = React.createRef<IFormIssuerRef>();
    const [isEnglish, setIsEnglish] = useState<boolean>(false);
    const issuerComponents: JSX.Element[] = [];

    useEffect(() => {
        const regulation = regulations.filter((element: IRegulationModel) => {
            return element.issuerId === selectedIssuer;
        });

        if (regulation.length) {
            ref.current?.setModalOpen(true, regulation.at(0)!.text, regulation.at(0)!.title);
        } else {
            ref.current?.setModalOpen(false);
        }
    }, [selectedIssuer]);

    useEffect(() => {
        async function getData() {
            let roleList = await getRoles(i18n.language);
            if (roleList) {
                const accountRole = account.role as IRoleModel;
                roleList = roleList.filter((role: IRoleModel) => {
                    if (accountRole.listCodes) {
                        const codes = accountRole.listCodes.split(',');
                        return codes.filter((element: string) => role.code === element).length > 0;
                    }
                    return true;
                });
                if (accountRole.listSkipCodes) {
                    const skipCodes = accountRole.listSkipCodes?.split(',');
                    roleList = roleList.filter((role: IRoleModel) => {
                        return !skipCodes?.includes(role.code);
                    });
                }
            }
            setRoles(roleList ?? []);

            const regulationList = await getRegulations(
                i18n.language,
                +(account!.province as IProvinceModel).id
            );
            setRegulations(regulationList ?? []);

            const issuerList = await getIssuers(
                i18n.language,
                Number(selectedRole),
                +(account!.province as IProvinceModel).id
            );
            setIssuers(issuerList ?? []);

            setIsEnglish(
                providerToDisplay ? providerToDisplay!.lng === 'en' : i18n.language === 'en'
            );
        }
        getData();
        const issueDateField = (document.getElementById(OrganizationStep3FormResultKeys.ISSUE_DATE + `_${index}`) as HTMLInputElement)!;
        handleCustomValidityOfDateFieldWithMinMax(t, issueDateField, 'form_date_minimum_error', 'form_date_maximum_error');
    }, [i18n.language, selectedRole]);

    const rolesComponents = roles.map((role: IRoleModel) => {
        return (
            <option key={role.id} value={role.id}>
                {role.name}
            </option>
        );
    });

    const handleOnChangeRole = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedRole(+e.target.value);
    };

    const handleOnChangeIssuer = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedIssuer(+e.target.value);
    };

    issuers
        ?.filter((issuer: IIssuerModel) => {
            return issuer.role.id === selectedRole.toString();
        })
        .forEach((issuer: IIssuerModel) => {
            issuerComponents.push(
                <option key={issuer.name} value={issuer.id}>
                    {issuer.name}
                </option>
            );
        });
    const providerToDisplay =
        providersOverwrite && providersOverwrite.length > index
            ? providersOverwrite[index]
            : account?.provider[index];
    const issueDate = providerToDisplay?.licenceDate
        ? new Date(providerToDisplay.licenceDate).toISOString().split('T')[0]
        : '';
    return (
        <>
            <FormIssuerModal ref={ref} />
            <div
                className="registration-organization-step-3-provider-container"
                id={`registration-organization-step-3-provider-${index}`}
                key={index}
            >
                <div className="registration-organization-step-3-provider-header">
                    {index === 0 ? (
                        <h1 className="header-3 header-3--purple">
                            {t('form_organization_step_3_provider_title').addSpace() + (index + 1)}
                        </h1>
                    ) : (
                        <div className="header-3 header-3--purple">
                            {t('form_organization_step_3_provider_title').addSpace() + (index + 1)}
                        </div>
                    )}
                    {!readonly && index !== 0 ? (
                        <img
                            data-id={index}
                            onClick={handleOnDeleteClick}
                            tabIndex={0}
                            aria-label={
                                t('form_organization_step_3_delete_alt_text', {
                                    ns: 'accessibility',
                                }).addSpace() +
                                t('form_organization_step_3_provider_title').addSpace() +
                                (index + 1)
                            }
                            id={`form_organization_step_3_delete_${index}`}
                            className="registration-organization-step-3-delete-logo"
                            src={iconDelete}
                            alt={t('form_organization_step_3_delete_alt_text', {
                                ns: 'accessibility',
                            })}
                            role="button"
                        />
                    ) : null}
                </div>

                <div className="registration-form-container">
                    <div className="registration-section">
                        <div className="registration-column-container">
                            <InputField
                                fieldContainerClassName="registration-column-item"
                                fieldName={OrganizationStep3FormResultKeys.FIRST_NAME + `_${index}`}
                                fieldTitle="form_organization_step_3_first_name"
                                fieldDisabled={readonly}
                                defaultValue={providerToDisplay?.firstname}
                                type="text"
                                fieldRequired
                                fieldWeight={index * 7}
                                maxLength={FORM_FIRSTNAME_MAX_LENGTH}
                                fieldHiddenLabel="form_organization_step_3_provider_title"
                            />
                            <InputField
                                fieldContainerClassName="registration-column-item"
                                fieldName={OrganizationStep3FormResultKeys.LAST_NAME + `_${index}`}
                                fieldTitle="form_organization_step_3_last_name"
                                fieldDisabled={readonly}
                                defaultValue={providerToDisplay?.lastname}
                                type="text"
                                fieldRequired
                                fieldWeight={1 + index * 7}
                                maxLength={FORM_LASTNAME_MAX_LENGTH}
                                fieldHiddenLabel="form_organization_step_3_provider_title"
                            />
                        </div>
                        <InputField
                            fieldContainerClassName="registration-field registration-column-item"
                            fieldName={OrganizationStep3FormResultKeys.EMAIL + `_${index}`}
                            fieldTitle="form_organization_step_3_email"
                            fieldDisabled={readonly}
                            defaultValue={providerToDisplay?.email}
                            type="email"
                            fieldRequired
                            fieldWeight={2 + index * 7}
                            fieldHiddenLabel="form_organization_step_3_provider_title"
                            fieldTooltip={
                                <PopoverContainer
                                    icon
                                    className={'field-tooltip-icon'}
                                >
                                    <>
                                    <p className="body-medium">
                                        {t('form_organization_step_3_email_popover')}
                                    </p>
                                    </>
                                </PopoverContainer>
                            }
                        />
                    </div>
                    <div className="registration-section">
                        <DropDownField
                            fieldContainerClassName="field-container--large registration-column-item"
                            fieldName={OrganizationStep3FormResultKeys.PROFESSION + `_${index}`}
                            fieldTitle="form_organization_step_3_profession"
                            fieldDefaultValue="form_organization_step_3_profession_default"
                            fieldOptions={rolesComponents}
                            fieldDisabled={readonly}
                            fieldRequired
                            value={(providerToDisplay?.role as IRoleModel)?.id ?? ''}
                            handleOnChange={handleOnChangeRole}
                            fieldWeight={3 + index * 7}
                            fieldHiddenLabel="form_organization_step_3_provider_title"
                        />

                        <div className="registration-column-container registration-preferred-lang-container">
                            <div className="body-medium">
                                {t('form_organization_step_3_correspondence_lang_title')}
                            </div>
                            <div className="registration-preferred-lang-selection">
                                <div className="radio-button-container">
                                    <input
                                        className="radio-button--input field-value"
                                        type="radio"
                                        value={ELanguage.English}
                                        id={`form_organization_step_3_correspondence_lang_english_${index}`}
                                        name={OrganizationStep3FormResultKeys.LNG + `_${index}`}
                                        onBlur={handleInputWithLabelOnBlur}
                                        onFocus={handleInputWithLabelOnFocus}
                                        disabled={readonly}
                                        checked={isEnglish}
                                        onChange={(e) => {
                                            e.target.checked && setIsEnglish(true);
                                        }}
                                    />
                                    <label
                                        htmlFor={`form_organization_step_3_correspondence_lang_english_${index}`}
                                        className={'radio-button radio-button--no-margin field-label registration-column-item '.concat(
                                            readonly ? 'field-label--disabled' : ''
                                        )}
                                    >
                                        <span className="accessibility-hidden">
                                            {t(
                                                'form_organization_step_3_correspondence_lang_title'
                                            )}
                                        </span>
                                        {t('form_organization_step_3_correspondence_lang_english')}
                                    </label>
                                </div>
                                <div className="radio-button-container">
                                    <input
                                        className="radio-button--input field-value"
                                        type="radio"
                                        value={ELanguage.French}
                                        id={`form_organization_step_3_correspondence_lang_french_${index}`}
                                        name={OrganizationStep3FormResultKeys.LNG + `_${index}`}
                                        onBlur={handleInputWithLabelOnBlur}
                                        onFocus={handleInputWithLabelOnFocus}
                                        disabled={readonly}
                                        checked={!isEnglish}
                                        onChange={(e) => {
                                            e.target.checked && setIsEnglish(false);
                                        }}
                                    />
                                    <label
                                        htmlFor={`form_organization_step_3_correspondence_lang_french_${index}`}
                                        className={'radio-button radio-button--no-margin field-label registration-column-item '.concat(
                                            readonly ? 'field-label--disabled' : ''
                                        )}
                                    >
                                        <span className="accessibility-hidden">
                                            {t(
                                                'form_organization_step_3_correspondence_lang_title'
                                            )}
                                        </span>
                                        {t('form_organization_step_3_correspondence_lang_french')}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="header-3 header-3--purple">
                    {t('form_organization_step_3_provider_license_title')}
                </div>
                <div className="registration-form-container">
                    <div className="registration-section">
                        {issuerComponents.length > 1 ? (
                            <DropDownField
                                fieldContainerClassName="registration-field"
                                fieldName={OrganizationStep3FormResultKeys.ISSUER + `_${index}`}
                                fieldTitle="form_organization_step_3_issuer"
                                handleOnChange={handleOnChangeIssuer}
                                fieldOptions={issuerComponents}
                                fieldDisabled={readonly}
                                fieldRequired
                                value={(providerToDisplay?.issuer as IIssuerModel)?.id ?? ''}
                                fieldWeight={4 + index * 7}
                                fieldHiddenLabel="form_organization_step_3_provider_title"
                            />
                        ) : (
                            <div className={'field-container registration-field'}>
                                <label
                                    htmlFor="form_organization_step_3_issuer"
                                    className={'field-label field-label-container '.concat(
                                        readonly ? 'field-label--disabled' : ''
                                    )}
                                >
                                    <>{t('form_organization_step_3_issuer')}</>
                                </label>
                                <select
                                    id="form_organization_step_3_issuer"
                                    name={OrganizationStep3FormResultKeys.ISSUER + `_${index}`}
                                    className="field-input field-input--dropdown field-value"
                                    disabled
                                    defaultValue={
                                        issuerComponents.length
                                            ? issuerComponents[0].props.value
                                            : ''
                                    }
                                >
                                    <option
                                        value={
                                            issuerComponents.length
                                                ? issuerComponents[0].props.value
                                                : ''
                                        }
                                        disabled
                                    >
                                        {issuerComponents.length ? issuerComponents[0].key : ''}
                                    </option>
                                </select>
                            </div>
                        )}
                    </div>
                    <div className="registration-section">
                        <div className="registration-column-container">
                            <div className="registration-column-item">
                                <InputField
                                    fieldContainerClassName="registration-field"
                                    fieldName={OrganizationStep3FormResultKeys.NUMBER + `_${index}`}
                                    fieldTitle="form_organization_step_3_number"
                                    fieldDisabled={readonly}
                                    defaultValue={providerToDisplay?.licenceNumber ?? ''}
                                    type="text"
                                    fieldRequired
                                    fieldWeight={5 + index * 7}
                                    fieldHiddenLabel="form_organization_step_3_provider_title"
                                />
                            </div>
                            <div className="registration-column-item">
                                <InputField
                                    fieldContainerClassName="registration-field"
                                    fieldName={
                                        OrganizationStep3FormResultKeys.ISSUE_DATE + `_${index}`
                                    }
                                    fieldTitle="form_organization_step_3_issue_date"
                                    fieldDisabled={readonly}
                                    type="date"
                                    min={getDateMinimumString()}
                                    max={getDateMaximumString()}
                                    handleOnChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        handleCustomValidityOfDateFieldWithMinMax(t, e.target, 'form_date_minimum_error', 'form_date_maximum_error')
                                    }}
                                    fieldInputClass="field-date"
                                    defaultValue={issueDate}
                                    fieldRequired
                                    fieldWeight={6 + index * 7}
                                    fieldSelectableLabel
                                    fieldHiddenLabel="form_organization_step_3_provider_title"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="field-container registration-field">
                    <label
                        htmlFor="form_organization_step_3_comment"
                        className={'field-label field-label-container '.concat(
                            readonly ? 'field-label--disabled' : ''
                        )}
                        aria-hidden
                        tabIndex={-1}
                    >
                        {t('form_organization_step_3_comment').addSpace()}
                        <span className="field-label--optional">
                            {t('form_organization_step_3_comment_optional')}
                        </span>
                    </label>
                    <textarea
                        name={OrganizationStep3FormResultKeys.COMMENT + `_${index}`}
                        id="form_organization_step_3_comment"
                        className={
                            'field-input field-value' + (readonly ? ' field-input--disabled' : '')
                        }
                        defaultValue={providerToDisplay?.comment ?? ''}
                        disabled={readonly}
                        aria-label={t('form_organization_step_3_comment')
                            .addSpace()
                            .concat(t('form_organization_step_3_comment_optional'))}
                    />
                </div>
            </div>
        </>
    );
};

export default withTranslation()(OrganizationStep3Provider);
